import { BUY, SELL, SHORT_SELL } from '../constants';
import type { FinancialInstrument } from '../players/types';
import type { Order } from '../orders/types';
import type { UnderlyingAsset } from '../underlying_assets/types';
import type {
  SimplifiedOptionInstrumentState,
  SimplifiedOptionOrderSnapshot,
} from './types';

export type SimplifiedOptionAction = {
  transaction: number;
  label: string;
  maxContracts?: number;
};

/** Identifies only the new class-19 resource so legacy Option routing stays exact. */
export const isSimplifiedOptionAsset = (
  asset?: Pick<UnderlyingAsset, 'resourcetype'>,
) => asset?.resourcetype === 'SimplifiedOption';

/** Recognizes persistent settlement quarantine so every order entry point uses the same disable guard. */
export const isSimplifiedOptionSettlementFailed = (
  state?: SimplifiedOptionInstrumentState,
) =>
  state?.settlement_failed === true ||
  state?.availability?.status === 'SETTLEMENT_FAILED';

/** Hides ordinary unavailable series but retains quarantined positions for visible failure audit. */
export const shouldShowSimplifiedOptionInMarkets = (
  instrument: FinancialInstrument,
) => {
  const state = instrument.simplified_option;
  if (!state) return false;
  return (
    isSimplifiedOptionSettlementFailed(state) ||
    state.availability?.available === true
  );
};

/** Converts controlled ledger units back to whole contracts for player-facing displays. */
export const controlledUnitsToContracts = (
  controlledUnits: number,
  multiplier: number,
) => (multiplier > 0 ? controlledUnits / multiplier : 0);

/** Returns non-crossing actions while applying option-sell and short-write permissions separately. */
export const simplifiedOptionActions = (
  positionContracts: number,
  allowSell: boolean,
  allowShortSell: boolean,
): SimplifiedOptionAction[] => {
  if (positionContracts > 0) {
    return [
      { transaction: BUY, label: 'Buy to add' },
      ...(allowSell
        ? [
            {
              transaction: SELL,
              label: 'Sell to reduce',
              maxContracts: positionContracts,
            },
          ]
        : []),
    ];
  }
  if (positionContracts < 0) {
    return [
      {
        transaction: BUY,
        label: 'Buy to cover',
        maxContracts: Math.abs(positionContracts),
      },
      ...(allowShortSell
        ? [{ transaction: SHORT_SELL, label: 'Write to add' }]
        : []),
    ];
  }
  return [
    { transaction: BUY, label: 'Buy to open' },
    ...(allowShortSell
      ? [{ transaction: SHORT_SELL, label: 'Write to open' }]
      : []),
  ];
};

/** Refines reduce/cover wording when the request exactly closes the current position. */
export const simplifiedOptionActionLabel = (
  action: SimplifiedOptionAction,
  requestedContracts: number,
) =>
  action.maxContracts === requestedContracts
    ? action.transaction === BUY
      ? 'Buy to close'
      : 'Sell to close'
    : action.label;

/** Joins hydrated player orders to the exact current position and excludes system settlement rows. */
export const currentSimplifiedOptionOrders = (
  orders: Order[],
  activePositionId: number | null,
) => {
  if (activePositionId == null) return [];
  return orders.filter(
    order =>
      order.position_id === activePositionId &&
      order.simplified_option_snapshot != null &&
      String(order.resourcetype) !== 'SimplifiedOptionSettlementOrder',
  );
};

/** Combines transaction direction with the frozen effect for clear audit wording. */
export const simplifiedOptionSnapshotActionLabel = (
  transaction: number,
  effect: string,
) => {
  if (transaction === BUY) {
    return effect === 'OPEN' || effect === 'ADD'
      ? `Buy to ${effect.toLowerCase()}`
      : effect === 'CLOSE'
        ? 'Buy to close'
        : 'Buy to cover';
  }
  if (transaction === SHORT_SELL) {
    return `Write to ${effect.toLowerCase()}`;
  }
  return effect === 'CLOSE' ? 'Sell to close' : 'Sell to reduce';
};

/** Reads the public snapshot alias first while remaining compatible with persisted audit field names. */
export const simplifiedOptionSnapshotContracts = (
  snapshot?: SimplifiedOptionOrderSnapshot | null,
) => snapshot?.contracts ?? snapshot?.requested_contracts ?? 0;

/** Reads the public expiry alias first so drawers and blotters share one contract boundary. */
export const simplifiedOptionSnapshotExpiry = (
  snapshot?: SimplifiedOptionOrderSnapshot | null,
) => snapshot?.expiry ?? snapshot?.t1_expiry ?? '-';

/** Accepts either the backend currency symbol or an older numeric currency id for safe display. */
export const simplifiedOptionCurrencySymbol = (
  nativeCurrency: number | string | null | undefined,
  currencies: Record<string, { symbol: string }>,
) =>
  typeof nativeCurrency === 'string'
    ? nativeCurrency
    : nativeCurrency == null
      ? ''
      : currencies[nativeCurrency]?.symbol || '';

/** Estimates controlled exposure from the frozen quote without presenting it as backend authority. */
export const estimatedControlledExposure = (
  contracts: number,
  multiplier: number,
  underlyingKind?: 'EQUITY' | 'BOND',
  normalizedSpot?: number | null,
) => {
  const controlledAmount = contracts * multiplier;
  if (underlyingKind === 'BOND') return controlledAmount;
  if (underlyingKind === 'EQUITY' && normalizedSpot != null) {
    return controlledAmount * normalizedSpot;
  }
  return null;
};
